import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import mealsData from "@/data/meals.json";
import { useInView } from "@/hooks/useInView";

interface Meal {
  id: number;
  image: string;
  title: string;
  description: string;
}

const MealsCarousel = () => {
  const meals: Meal[] = mealsData;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const { ref: headerRef, isInView: headerInView } = useInView({ threshold: 0.2 });
  const { ref: carouselRef, isInView: carouselInView } = useInView({ threshold: 0.1 });
  const { ref: thumbnailsRef, isInView: thumbnailsInView } = useInView({ threshold: 0.2 });

  // Auto-slide functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === meals.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, meals.length]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? meals.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) =>
      prevIndex === meals.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container-wide px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-1000 ${
            headerInView
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground mb-4">
            Our Delicious <span className="text-primary">Meals</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore our carefully crafted, nutritious meals made with fresh,
            locally-sourced ingredients
          </p>
        </div>

        {/* Carousel Container */}
        <div
          ref={carouselRef}
          className={`relative max-w-5xl mx-auto transition-all duration-1000 ${
            carouselInView
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          {/* Main Image Display */}
          <div
            className={`relative overflow-hidden rounded-3xl shadow-card-hover transition-all duration-1000 ${
              carouselInView
                ? "scale-100 rotate-0"
                : "scale-95 rotate-2"
            }`}
          >
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {meals.map((meal) => (
                <div key={meal.id} className="min-w-full">
                  <div className="relative aspect-[16/9] md:aspect-[21/9]">
                    <img
                      src={meal.image}
                      alt={meal.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Overlay with meal info */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-8">
                      <h3 className="text-2xl md:text-3xl font-bold font-heading text-white mb-2">
                        {meal.title}
                      </h3>
                      <p className="text-white/90 font-body text-lg">
                        {meal.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-foreground rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Previous slide"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-foreground rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Next slide"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Dots Indicator */}
          <div
            className={`flex justify-center gap-3 mt-8 transition-all duration-700 delay-300 ${
              carouselInView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-5"
            }`}
          >
            {meals.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary ${
                  index === currentIndex
                    ? "bg-primary w-12 h-3"
                    : "bg-gray-300 hover:bg-gray-400 w-3 h-3"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Thumbnail Preview (Hidden on mobile) */}
          <div
            ref={thumbnailsRef}
            className="hidden md:grid grid-cols-5 gap-4 mt-8"
          >
            {meals.map((meal, index) => (
              <button
                key={meal.id}
                onClick={() => goToSlide(index)}
                className={`relative rounded-xl overflow-hidden transition-all duration-700 focus:outline-none focus:ring-2 focus:ring-primary ${
                  index === currentIndex
                    ? "ring-4 ring-primary scale-105"
                    : "opacity-60 hover:opacity-100"
                } ${
                  thumbnailsInView
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-10"
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="aspect-[4/3]">
                  <img
                    src={meal.image}
                    alt={meal.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Auto-play indicator */}
        <div className="text-center mt-8">
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            {isAutoPlaying ? "⏸ Pause Auto-play" : "▶ Resume Auto-play"}
          </button>
        </div>
      </div>
    </section>
  );
};

export default MealsCarousel;
